import type { TextAsset } from '@lumen5/luminary';

type Range = [number, number];

/**
 * The inline formatting a TEXT asset carries, as clean text plus one range array per kind. Every
 * range is a `[start, end]` pair of indices into `text`. Highlight is the brand-meaningful emphasis
 * (written as `**…**`); bold/italic/underline are the ordinary text styles (written as `<b>`, `<i>`,
 * `<u>` tags) and may overlap each other and the highlight freely.
 */
export interface TextFormatting {
    text: string;
    highlights: Range[];
    bold: Range[];
    italic: Range[];
    underline: Range[];
}

type FormattingType = 'highlight' | 'bold' | 'italic' | 'underline';

// Highlight opens and closes with the same `**`; the B/I/U tags have distinct open/close forms so
// they can overlap the highlight and each other without ambiguity.
const OPEN_MARKER: Record<FormattingType, string> = {
    highlight: '**',
    bold: '<b>',
    italic: '<i>',
    underline: '<u>',
};
const CLOSE_MARKER: Record<FormattingType, string> = {
    highlight: '**',
    bold: '</b>',
    italic: '</i>',
    underline: '</u>',
};

// Highlight is emitted outermost so its `**` wraps the tags, matching the historical single-type
// output; on close we walk this order in reverse so spans nest properly.
const TYPE_ORDER: FormattingType[] = ['highlight', 'bold', 'italic', 'underline'];

type MarkerDef = { seq: string; type: FormattingType; kind: 'toggle' | 'open' | 'close' };

// Longer close tags are listed before their open form so a scan can't mistake `</b>` for `<b>`.
const PARSE_MARKERS: MarkerDef[] = [
    { seq: '**', type: 'highlight', kind: 'toggle' },
    { seq: '</b>', type: 'bold', kind: 'close' },
    { seq: '<b>', type: 'bold', kind: 'open' },
    { seq: '</i>', type: 'italic', kind: 'close' },
    { seq: '<i>', type: 'italic', kind: 'open' },
    { seq: '</u>', type: 'underline', kind: 'close' },
    { seq: '<u>', type: 'underline', kind: 'open' },
];

// Escaped per character as the text is emitted, so the markers we splice in are never themselves
// escaped. `<`/`>` are escaped so literal angle brackets in user copy can't be misread as B/I/U tags
// on the way back in.
function escapeChar(char: string): string {
    switch (char) {
        case '\\':
            return '\\\\';
        case '\n':
            return '\\n';
        case '\r':
            return '\\r';
        case '\t':
            return '\\t';
        case '<':
            return '\\<';
        case '>':
            return '\\>';
        default:
            return char;
    }
}

// Trim tabs/newlines/carriage-returns off a highlight's edges (spaces are kept, to support subword
// highlights). Historical behaviour, preserved so a highlight range that happens to include edge
// whitespace still emits its `**` around the visible content.
function trimHighlightRange(text: string, [start, end]: Range): Range {
    let trimmedStart = start;
    let trimmedEnd = end;
    while (trimmedStart < trimmedEnd && /[\t\n\r]/.test(text[trimmedStart])) {
        trimmedStart++;
    }
    while (trimmedEnd > trimmedStart && /[\t\n\r]/.test(text[trimmedEnd - 1])) {
        trimmedEnd--;
    }
    return [trimmedStart, trimmedEnd];
}

/**
 * Parse a formatting-marked string into clean text plus a range array per formatting kind. Handles
 * `**highlight**` and the `<b>`/`<i>`/`<u>` tags in a single pass, using an open-position stack per
 * kind so overlapping / improperly-nested spans (e.g. `**<b>word**</b>`) still parse — each kind is
 * paired independently. Markers that never pair up are left as literal text, and `\<`/`\>` are
 * unescaped back into literal angle brackets.
 */
export function getTextAndFormattingFromMarkup(markup: string): TextFormatting {
    type Token = { literal: string } | { marker: MarkerDef };
    const tokens: Token[] = [];

    let i = 0;
    while (i < markup.length) {
        const char = markup[i];
        if (char === '\\' && (markup[i + 1] === '<' || markup[i + 1] === '>')) {
            tokens.push({ literal: markup[i + 1] });
            i += 2;
            continue;
        }
        const marker = PARSE_MARKERS.find((definition) => markup.startsWith(definition.seq, i));
        if (marker) {
            tokens.push({ marker });
            i += marker.seq.length;
            continue;
        }
        tokens.push({ literal: char });
        i += 1;
    }

    const activeMarkers = new Set<number>();
    const pairs: { type: FormattingType; openIndex: number; closeIndex: number }[] = [];
    const openStacks: Record<'bold' | 'italic' | 'underline', number[]> = {
        bold: [],
        italic: [],
        underline: [],
    };
    let highlightOpen: number | null = null;
    // Whether a literal character has appeared since the pending `**` opener. A highlight only closes
    // when its pair wraps real content — mirroring the old `/\*\*([^*]+)\*\*/` regex, which required a
    // non-`*` char inside. Without this, runs like `****` would be consumed and deleted from the text.
    let highlightSawLiteral = false;

    tokens.forEach((token, index) => {
        if (!('marker' in token)) {
            if (highlightOpen !== null) {
                highlightSawLiteral = true;
            }
            return;
        }
        const { marker } = token;
        if (marker.kind === 'toggle') {
            if (highlightOpen === null) {
                highlightOpen = index;
                highlightSawLiteral = false;
            } else if (highlightSawLiteral) {
                pairs.push({ type: 'highlight', openIndex: highlightOpen, closeIndex: index });
                activeMarkers.add(highlightOpen).add(index);
                highlightOpen = null;
                highlightSawLiteral = false;
            } else {
                // Empty pair (`**` immediately followed by `**`): the earlier opener never wrapped
                // content, so leave it as literal text and treat this `**` as a fresh opener. A run
                // like `****` thus stays `****` instead of vanishing.
                highlightOpen = index;
                highlightSawLiteral = false;
            }
        } else if (marker.kind === 'open') {
            openStacks[marker.type as 'bold' | 'italic' | 'underline'].push(index);
        } else {
            const stack = openStacks[marker.type as 'bold' | 'italic' | 'underline'];
            const openIndex = stack.pop();
            if (openIndex !== undefined) {
                pairs.push({ type: marker.type, openIndex, closeIndex: index });
                activeMarkers.add(openIndex).add(index);
            }
        }
    });

    let text = '';
    const cleanPosition = new Map<number, number>();
    tokens.forEach((token, index) => {
        if ('literal' in token) {
            text += token.literal;
        } else if (activeMarkers.has(index)) {
            cleanPosition.set(index, text.length);
        } else {
            text += token.marker.seq;
        }
    });

    const result: TextFormatting = { text, highlights: [], bold: [], italic: [], underline: [] };
    const buckets: Record<FormattingType, Range[]> = {
        highlight: result.highlights,
        bold: result.bold,
        italic: result.italic,
        underline: result.underline,
    };
    for (const pair of pairs) {
        let start = cleanPosition.get(pair.openIndex);
        const end = cleanPosition.get(pair.closeIndex);
        if (start === undefined || end === undefined) {
            continue;
        }
        // A highlight's content can't begin with `*` (the old regex matched `[^*]+`), so a `***` run
        // before content is a literal `*` plus the `**` opener. Advance past any leading `*` so the
        // highlight lands on the real content, not the stray star — otherwise `***New***` would
        // highlight `*New` and a serialize→parse round trip would drift the range.
        if (pair.type === 'highlight') {
            while (start < end && text[start] === '*') {
                start++;
            }
        }
        if (end > start) {
            buckets[pair.type].push([start, end]);
        }
    }
    for (const type of TYPE_ORDER) {
        buckets[type].sort((a, b) => a[0] - b[0]);
    }
    return result;
}

/**
 * Serialize a TEXT asset's clean text and its four formatting range arrays back into a marked
 * string: `**highlight**` and the `<b>`/`<i>`/`<u>` tags. Markers are inserted by walking every
 * range's open/close boundary in position order, so overlapping spans of different kinds all land
 * correctly (the end-first single-type slice trick doesn't generalise to four overlapping types).
 * Literal `<`/`>` and the existing `\n`/`\t` characters are escaped so they read back unchanged.
 */
export function getTextWithFormattingAsMarkup(asset: TextAsset): string {
    const text = asset.text;
    const opensAt = new Map<number, FormattingType[]>();
    const closesAt = new Map<number, FormattingType[]>();

    const addRange = (type: FormattingType, [start, end]: Range): void => {
        if (end <= start) {
            return;
        }
        const opens = opensAt.get(start) ?? [];
        opens.push(type);
        opensAt.set(start, opens);
        const closes = closesAt.get(end) ?? [];
        closes.push(type);
        closesAt.set(end, closes);
    };

    asset.textHighlights.forEach((range) => addRange('highlight', trimHighlightRange(text, range)));
    asset.textBold.forEach((range) => addRange('bold', [range[0], range[1]]));
    asset.textItalic.forEach((range) => addRange('italic', [range[0], range[1]]));
    asset.textUnderline.forEach((range) => addRange('underline', [range[0], range[1]]));

    let markup = '';
    for (let position = 0; position <= text.length; position++) {
        const closing = closesAt.get(position);
        if (closing) {
            for (let index = TYPE_ORDER.length - 1; index >= 0; index--) {
                if (closing.includes(TYPE_ORDER[index])) {
                    markup += CLOSE_MARKER[TYPE_ORDER[index]];
                }
            }
        }
        const opening = opensAt.get(position);
        if (opening) {
            for (const type of TYPE_ORDER) {
                if (opening.includes(type)) {
                    markup += OPEN_MARKER[type];
                }
            }
        }
        if (position < text.length) {
            markup += escapeChar(text[position]);
        }
    }
    return markup;
}
